import type { QboWebhookModel, ClioWebhookModel } from '@xtnd/web/shared/types/tenantSettingTypes';

// Clio Webhook Models from https://docs.developers.clio.com/api-reference/#tag/Webhooks
export const CLIO_WEBHOOK_MODELS: ClioWebhookModel[] = [
  {
    name: 'Bill',
    displayName: 'Bills',
    events: ['created', 'updated', 'deleted'],
  },
  {
    name: 'Activity',
    displayName: 'Activities (Time Entries)',
    events: ['created', 'updated', 'deleted'],
  },
  {
    name: 'Contact',
    displayName: 'Contacts',
    events: ['created', 'updated', 'deleted'],
  },
  {
    name: 'Document',
    displayName: 'Documents',
    events: ['created', 'updated', 'deleted'],
  },
  {
    name: 'Communication',
    displayName: 'Communications',
    events: ['created', 'updated', 'deleted'],
  },
  {
    name: 'CalendarEntry',
    displayName: 'Calendar Entries',
    events: ['created', 'updated', 'deleted'],
  },
  {
    name: 'Task',
    displayName: 'Tasks',
    events: ['created', 'updated', 'deleted'],
  },
  {
    name: 'Note',
    displayName: 'Notes',
    events: ['created', 'updated', 'deleted'],
  },
  {
    name: 'ExpenseEntry',
    displayName: 'Expense Entries',
    events: ['created', 'updated', 'deleted'],
  },
  {
    name: 'Trust',
    displayName: 'Trust Transactions',
    events: ['created', 'updated', 'deleted'],
  },
  // Special cases with different event patterns
  {
    name: 'Matter',
    displayName: 'Matters',
    events: ['opened', 'closed', 'updated'],
    special: true,
  },
  {
    name: 'ClioPaymentsPayment',
    displayName: 'Clio Payments',
    events: ['completed', 'failed', 'cancelled'],
    special: true,
  },
];

// QBO Webhook Models from QuickBooks Online API documentation
export const QBO_WEBHOOK_MODELS: QboWebhookModel[] = [
  {
    name: 'Customer',
    displayName: 'Customers',
    events: ['Create', 'Update', 'Delete', 'Merge'],
  },
  {
    name: 'Invoice',
    displayName: 'Invoices',
    events: ['Create', 'Update', 'Delete', 'Void'],
  },
  {
    name: 'Payment',
    displayName: 'Payments',
    events: ['Create', 'Update', 'Delete', 'Void'],
  },
  {
    name: 'Bill',
    displayName: 'Bills',
    events: ['Create', 'Update', 'Delete', 'Void'],
  },
  {
    name: 'BillPayment',
    displayName: 'Bill Payments',
    events: ['Create', 'Update', 'Delete', 'Void'],
  },
  {
    name: 'Item',
    displayName: 'Items',
    events: ['Create', 'Update', 'Delete'],
  },
  {
    name: 'Account',
    displayName: 'Accounts',
    events: ['Create', 'Update', 'Delete'],
  },
  {
    name: 'Estimate',
    displayName: 'Estimates',
    events: ['Create', 'Update', 'Delete'],
  },
  {
    name: 'SalesReceipt',
    displayName: 'Sales Receipts',
    events: ['Create', 'Update', 'Delete', 'Void'],
  },
  {
    name: 'CreditMemo',
    displayName: 'Credit Memos',
    events: ['Create', 'Update', 'Delete', 'Void'],
  },
  {
    name: 'RefundReceipt',
    displayName: 'Refund Receipts',
    events: ['Create', 'Update', 'Delete', 'Void'],
  },
  {
    name: 'JournalEntry',
    displayName: 'Journal Entries',
    events: ['Create', 'Update', 'Delete'],
  },
  {
    name: 'TimeActivity',
    displayName: 'Time Activities',
    events: ['Create', 'Update', 'Delete'],
  },
  {
    name: 'Vendor',
    displayName: 'Vendors',
    events: ['Create', 'Update', 'Delete'],
  },
  {
    name: 'Employee',
    displayName: 'Employees',
    events: ['Create', 'Update', 'Delete'],
  },
];

// Simplified webhook models for ClioWebhookLifecycleManager
export const WEBHOOK_MODELS = [
  { name: 'bill', label: 'Bills', events: ['created', 'updated', 'deleted'] },
  { name: 'matter', label: 'Matters', events: ['matter_opened', 'matter_pended', 'matter_closed'] },
  { name: 'contact', label: 'Contacts', events: ['created', 'updated', 'deleted'] },
  { name: 'activity', label: 'Activities', events: ['created', 'updated', 'deleted'] },
];

// Helper functions
export const getDefaultClioWebhookSubscriptions = () => {
  return CLIO_WEBHOOK_MODELS.map(model => ({
    modelName: model.name,
    events: [], // Start with no events selected
    enabled: false,
  }));
};

export const getDefaultQboWebhookSubscriptions = () => {
  return QBO_WEBHOOK_MODELS.map(model => ({
    modelName: model.name,
    events: [], // Start with no events selected
    enabled: false,
  }));
};

// QBO webhook models for settings page
export const QBO_WEBHOOK_SETTINGS_MODELS = [
  { name: 'invoice', label: 'Invoice', description: 'Invoice creation, updates, and deletions' },
  { name: 'customer', label: 'Customer', description: 'Customer information changes' },
  { name: 'item', label: 'Item', description: 'Product and service item changes' },
  { name: 'payment', label: 'Payment', description: 'Payment transactions' },
  { name: 'bill', label: 'Bill', description: 'Vendor bill changes' },
];

